/**
 * 本機日期時間字串的拆合（2026-08-29）
 *
 * 背景：建單原本用單一 `<input type="datetime-local">`。Chrome 的日期選擇器在值為空時，
 * 使用者一挑日期，**時間會被自動填成「開啟表單當下的時刻」** —— 使用者以為自己只選了日期，
 * 實際上送出了一個錯的時間。訂單 ORD260820ZU823K 的上車時間變成 08:55（＝客戶開表單的時刻，
 * 而非航班 CX402 的抵達時間 20:05）就是這樣來的。
 *
 * 改為日期、時間兩個欄位：`type="date"` 與 `type="time"` 都不會替使用者猜值，
 * 時間沒填就是空的，驗證擋得住。這裡放拆／合的純函式。
 */

/**
 * "2026-08-21T20:05" → { date: "2026-08-21", time: "20:05" }
 *
 * 也接受「只填一半」的中間值（"2026-08-21T" 或 "T20:05"）—— 使用者一定是先填其中一個，
 * 若中間值無法保存，剛輸入的日期會在下一次重繪時被清掉。
 */
export function splitLocalDateTime(value: string): { date: string; time: string } {
  const [rawDate = "", rawTime = ""] = value.trim().split("T");
  const date = /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : "";
  const tm = /^(\d{2}:\d{2})/.exec(rawTime);
  return { date, time: tm ? tm[1]! : "" };
}

/**
 * 組回單一字串。**允許只有一半**：只有日期回 "2026-08-21T"、只有時間回 "T20:05"。
 * 這種中間值餵給 `new Date()` 會得到 Invalid Date，既有的
 * `Number.isFinite(pickupMs)` 驗證照樣擋得住，不會被誤當成有效時間送出。
 * 兩者皆空才回空字串。
 */
export function joinLocalDateTime(date: string, time: string): string {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(date.trim()) ? date.trim() : "";
  // 部分瀏覽器的 time input 會帶秒（HH:mm:ss），只取到分
  const tm = /^(\d{2}:\d{2})/.exec(time.trim());
  const t = tm ? tm[1]! : "";
  if (d === "" && t === "") return "";
  return `${d}T${t}`;
}

/** 日期與時間都齊全才算完整（可送出） */
export function isCompleteLocalDateTime(value: string): boolean {
  const { date, time } = splitLocalDateTime(value);
  return date !== "" && time !== "";
}

/** 給 date input 的 min：今天（本機時區）。now 由呼叫端傳入，保持純函式。 */
export function todayLocalDate(nowMs: number): string {
  const d = new Date(nowMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
